export type PlanTablePositionAnchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

export const DEFAULT_PLAN_TABLE_POSITION_ANCHOR: PlanTablePositionAnchor = 'top-left'

export interface PlanTablePosition2D {
  x: number
  y: number
}

export interface PlanTableSize2D {
  width: number
  height: number
}

export interface PlanTableBounds2D {
  xMin: number
  yMin: number
  xMax: number
  yMax: number
  width: number
  height: number
}

/**
 * Axis-aligned bounds for a table whose `position` is the pinned corner given by
 * `positionAnchor`.
 */
export const computePlanTableBounds = (
  position: PlanTablePosition2D,
  size: PlanTableSize2D,
  positionAnchor: PlanTablePositionAnchor,
): PlanTableBounds2D => {
  const { x, y } = position
  const { width, height } = size

  let xMin: number
  let yMin: number

  switch (positionAnchor) {
    case 'top-left':
      xMin = x
      yMin = y - height
      break
    case 'top-right':
      xMin = x - width
      yMin = y - height
      break
    case 'bottom-right':
      xMin = x - width
      yMin = y
      break
    case 'bottom-left':
    default:
      xMin = x
      yMin = y
      break
  }

  return {
    xMin,
    yMin,
    xMax: xMin + width,
    yMax: yMin + height,
    width,
    height,
  }
}

/** Bottom-left corner of the drawable rect — used by PDF layout which is BL-based. */
export const computePlanTableBottomLeftOrigin = (
  position: PlanTablePosition2D,
  size: PlanTableSize2D,
  positionAnchor: PlanTablePositionAnchor,
): PlanTablePosition2D => {
  const bounds = computePlanTableBounds(position, size, positionAnchor)
  return { x: bounds.xMin, y: bounds.yMin }
}

/** Position of a given corner of an existing bounds rect (for remapping on anchor change). */
export const positionForPlanTableAnchor = (
  bounds: PlanTableBounds2D,
  positionAnchor: PlanTablePositionAnchor,
): PlanTablePosition2D => {
  switch (positionAnchor) {
    case 'top-left':
      return { x: bounds.xMin, y: bounds.yMax }
    case 'top-right':
      return { x: bounds.xMax, y: bounds.yMax }
    case 'bottom-right':
      return { x: bounds.xMax, y: bounds.yMin }
    case 'bottom-left':
    default:
      return { x: bounds.xMin, y: bounds.yMin }
  }
}

/**
 * Keep the visual rect fixed while switching which corner `position` refers to.
 */
export const remapPlanTablePositionForAnchorChange = (
  position: PlanTablePosition2D,
  size: PlanTableSize2D,
  fromAnchor: PlanTablePositionAnchor,
  toAnchor: PlanTablePositionAnchor,
): PlanTablePosition2D => {
  if (fromAnchor === toAnchor) return position
  const bounds = computePlanTableBounds(position, size, fromAnchor)
  return positionForPlanTableAnchor(bounds, toAnchor)
}
